import { useCallback, useState } from "react"
import { DateRange } from "react-day-picker"
import { supabase } from "@/lib/data"
import type { EvalItemMetaMap } from "@/pages/dev/moderation/types"
import { isModerationFlagItemId } from "@/pages/dev/moderation/utils/conversationUtils"
import type { RawContact, RawMessage } from "../components/types"

export type DashboardContactSourceFilter = "real_only" | "synthetic_only" | "all"

function toUnix(value?: string | null): number {
  if (!value) return 0
  const ts = new Date(value).getTime()
  return Number.isFinite(ts) ? Math.floor(ts / 1000) : 0
}

function parseContactVars(row: Record<string, any>): Record<string, any> {
  let fromData: Record<string, any> = {}
  if (typeof row.data === "string" && row.data.trim()) {
    try {
      fromData = JSON.parse(row.data)
    } catch {
      fromData = {}
    }
  } else if (row.data && typeof row.data === "object") {
    fromData = row.data
  }

  // New extraction payload column will be merged automatically when available.
  const fromExtractions = row.extractions && typeof row.extractions === "object"
    ? row.extractions
    : {}

  return {
    ...fromData,
    extractions: fromExtractions,
    handled_by: fromData.handled_by || "bot",
    internal_comments: row.internal_comments ?? fromData.internal_comments ?? null,
  }
}

// Batched by 100 ids to stay under URL limits, mirroring the contacts fetch.
// Loads ALL of the team's form records (not just for in-range contacts), so a
// captured form surfaces on the dashboard even if the contact who filled it
// didn't send a message inside the selected date range. Flattens each record's
// values to "form.<field>" keyed by contact, and returns the set of contacts
// that have form data so the caller can ensure those contacts are loaded too.
async function fetchTeamFormValues(
  teamId: string,
  activeContactIds: string[]
): Promise<{ valuesByContact: Map<string, Record<string, any>>; formIds: Set<string>; contactIds: Set<string>; updatedAt: Record<string, string> }> {
  const valuesByContact = new Map<string, Record<string, any>>()
  // Latest record update per "<contact>:<form>", for the Forms tab export
  const updatedAt: Record<string, string> = {}
  const formIds = new Set<string>()
  const contactIds = new Set<string>()
  if (!teamId) return { valuesByContact, formIds, contactIds, updatedAt }

  const ingest = (rows: any[]) => {
    for (const row of rows) {
      if (!row.contact) continue
      contactIds.add(row.contact)
      if (row.form) formIds.add(row.form)
      if (row.form && row.updated_at) {
        const k = `${row.contact}:${row.form}`
        if (!updatedAt[k] || row.updated_at > updatedAt[k]) updatedAt[k] = row.updated_at
      }
      const values = (row.values as Record<string, any>) || {}
      const flat: Record<string, any> = valuesByContact.get(row.contact) || {}
      for (const [k, v] of Object.entries(values)) flat[`form.${k}`] = v
      valuesByContact.set(row.contact, flat)
    }
  }

  // Primary: page through all form_records for the team (PostgREST caps at 1000).
  const PAGE = 1000
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await supabase
      .from("form_records")
      .select("contact, form, values, updated_at")
      .eq("team", teamId)
      .range(offset, offset + PAGE - 1)
    if (error) {
      console.warn("[dashboard] could not load team form records:", error.message)
      break
    }
    const rows = data || []
    ingest(rows)
    if (rows.length < PAGE) break
  }

  // Backfill: some records were written with a null team, so the team filter
  // misses them. Also fetch by the in-range contact ids to catch those.
  const BATCH = 100
  for (let i = 0; i < activeContactIds.length; i += BATCH) {
    const batch = activeContactIds.slice(i, i + BATCH)
    const { data, error } = await supabase
      .from("form_records")
      .select("contact, form, values, updated_at")
      .in("contact", batch)
    if (error) break
    ingest(data || [])
  }

  return { valuesByContact, formIds, contactIds, updatedAt }
}

export interface DashboardFormFieldMeta {
  /** Human-readable field label; falls back to the field name. */
  title: string
  type?: string
  /** Maps a stored option value → its display label, for list/multiselect fields. */
  optionLabels?: Record<string, string>
  /** Id of the form this field belongs to, for grouping in the UI. */
  formId?: string
  /** Title of the form this field belongs to. */
  formTitle?: string
}

// Loads the team's form definitions and flattens their fields into a lookup
// keyed by the dashboard-namespaced variable key ("form.<fieldName>"). Group
// subfields are included as "form.<groupName>.<subName>" so nested labels
// resolve too. Each entry carries its owning form's id/title so the dashboard
// can group variables by form. Loading ALL team forms (not just ones referenced
// by in-range records) means field titles resolve even before any records load.
async function fetchFormFieldMeta(teamId: string, referencedFormIds: string[] = []): Promise<Record<string, DashboardFormFieldMeta>> {
  const meta: Record<string, DashboardFormFieldMeta> = {}
  if (!teamId && referencedFormIds.length === 0) return meta

  // Load form definitions two ways and merge: by team, AND by the exact form ids
  // referenced by the loaded records. The id-based fetch is what guarantees we
  // get the definition for every record we have values for — even if that form's
  // `team` column is null or mismatched, which would make a team-only query miss it.
  const formsById = new Map<string, any>()
  const collect = (rows: any[] | null) => {
    for (const f of rows || []) if (f?.id) formsById.set(f.id, f)
  }

  if (teamId) {
    const { data, error } = await supabase
      .from("forms")
      .select("id, title, fields")
      .eq("team", teamId)
    if (error) console.warn("[dashboard] could not load team form definitions:", error.message)
    collect(data)
  }

  if (referencedFormIds.length > 0) {
    const BATCH = 100
    for (let i = 0; i < referencedFormIds.length; i += BATCH) {
      const batch = referencedFormIds.slice(i, i + BATCH)
      const { data, error } = await supabase
        .from("forms")
        .select("id, title, fields")
        .in("id", batch)
      if (error) { console.warn("[dashboard] could not load referenced form definitions:", error.message); break }
      collect(data)
    }
  }

  const data = Array.from(formsById.values())
  if (data.length === 0) return meta

  const addField = (field: any, prefix: string, formId: string, formTitle: string) => {
    if (!field || typeof field !== "object" || !field.name) return
    const key = `${prefix}${field.name}`
    const optionLabels: Record<string, string> = {}
    if (Array.isArray(field.list)) {
      for (const opt of field.list) {
        if (opt && opt.value != null) optionLabels[String(opt.value)] = String(opt.label ?? opt.value)
      }
    }
    meta[key] = {
      title: field.title || field.name,
      type: field.type,
      optionLabels: Object.keys(optionLabels).length ? optionLabels : undefined,
      formId,
      formTitle,
    }
    if (field.type === "group" && Array.isArray(field.subfields)) {
      for (const sub of field.subfields) addField(sub, `${key}.`, formId, formTitle)
    }
  }

  for (const form of data) {
    const fields = Array.isArray(form.fields) ? form.fields : []
    const formTitle = form.title || "(untitled form)"
    for (const field of fields) addField(field, "form.", form.id, formTitle)
  }
  return meta
}

// PostgREST's default max_rows is 1000. Requesting more than that causes the server
// to return exactly 1000, which the pagination loop misreads as "last page".
// Keep page sizes at or below 1000 so the "rows.length < PAGE_SIZE" sentinel works correctly.
const CONTACT_PAGE_SIZE = 1000
const MESSAGE_PAGE_SIZE = 1000
// How many 1k-row message pages to request in parallel. PostgREST caps each
// response at 1000 rows, so we can't ask for bigger pages — but the requests are
// independent, so fetching a window of them concurrently cuts wall-clock roughly
// PAGE_CONCURRENCY-fold. Kept modest to avoid hammering the DB / hitting rate limits.
const PAGE_CONCURRENCY = 5
// Hard ceiling on how many message rows we pull into the browser for client-side
// analytics. This bounds memory + compute; beyond it we stop paging and flag the
// result as truncated so the UI can warn that stats cover only the most recent N.
const MAX_MESSAGE_ROWS = 100_000

type DashboardFlagKind = "high_risk" | "low_confidence" | "asked_human"

function contributingIdsForCategory(meta: EvalItemMetaMap, kind: DashboardFlagKind): number[] {
  return Object.keys(meta)
    .map(Number)
    .filter((id) => {
      const m = meta[id]
      if (!m) return false
      if (kind === "high_risk") return m.highrisk === true
      if (kind === "low_confidence") return m.lowconf === true
      return m.askhuman === true
    })
    .sort((a, b) => a - b)
}

function aggregateCategoryVars(
  userItems: any[],
  getItemStatus: (itemId: number) => string | null,
  meta: EvalItemMetaMap,
  kind: DashboardFlagKind
): "flagged" | "resolved" | null {
  let active = false
  let resolved = false
  for (const id of contributingIdsForCategory(meta, kind)) {
    const st = getItemStatus(id)
    if (st === null) continue
    if (st === "resolved") resolved = true
    else active = true
  }
  if (active) return "flagged"
  if (resolved) return "resolved"
  return null
}

function mapMessageFlags(row: Record<string, any>, evalMeta: EvalItemMetaMap): Record<string, any> {
  const parseItems = (raw: unknown): any[] => {
    if (typeof raw === "string") {
      try {
        const p = JSON.parse(raw)
        return Array.isArray(p) ? p : []
      } catch {
        return []
      }
    }
    return Array.isArray(raw) ? raw : []
  }

  const parseCustomFlags = (raw: unknown): Array<{ flagId: string; status?: string | null }> => {
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    return Array.isArray(raw) ? raw as Array<{ flagId: string; status?: string | null }> : []
  }

  const userItems = parseItems(row.user_detected_items)
  const evalItems = [...userItems, ...parseItems(row.agent_detected_items)]
  const customFlags = parseCustomFlags(row.custom_message_flags)
  const activeFlagIds = new Set<string>()
  const resolvedFlagIds = new Set<string>()

  const applyBuiltinFlagState = (flagId: string, value: unknown) => {
    if (value === 1) activeFlagIds.add(flagId)
    if (value === 2) resolvedFlagIds.add(flagId)
  }

  applyBuiltinFlagState("high_risk", row.highrisk)
  applyBuiltinFlagState("low_confidence", row.lowconf)
  applyBuiltinFlagState("asked_human", row.askhuman)

  customFlags.forEach((flag) => {
    const flagId = String(flag?.flagId || "").trim()
    if (!flagId) return
    if (String(flag.status || "").toLowerCase() === "resolved") resolvedFlagIds.add(flagId)
    else activeFlagIds.add(flagId)
  })

  const getItemStatus = (itemId: number): string | null => {
    const match = evalItems.find((it: any) => {
      const rawId = it?.itemId ?? it?.item_id ?? it?.id
      return Number(rawId) === itemId
    })
    if (!match) return null
    const normalized = String(match?.status || "").toLowerCase().trim()
    return normalized || "flagged"
  }

  // No backwards-compat mode: escalation is driven by active flags only.
  const hasAnyActiveFlag = activeFlagIds.size > 0

  const categories = new Set<string>()
  for (const item of userItems) {
    const id = Number(item?.itemId ?? item?.item_id ?? item?.id)
    if (!Number.isFinite(id) || isModerationFlagItemId(id, evalMeta)) continue
    const name = evalMeta[id]?.name
    if (name) categories.add(name)
  }

  const vars: Record<string, any> = {
    rating: row.rating ?? null,
    rating_example: row.rating_example ?? null,
    escalation: hasAnyActiveFlag ? "true" : "false",
    risk_category_level: row.escalation_to_level != null ? String(row.escalation_to_level) : null,
    ai_explanation: row.escalation_reasoning ?? null,
    high_risk_flag: activeFlagIds.has("high_risk") ? "flagged" : (resolvedFlagIds.has("high_risk") ? "resolved" : null),
    asked_human_flag: activeFlagIds.has("asked_human") ? "flagged" : (resolvedFlagIds.has("asked_human") ? "resolved" : null),
    low_confidence_flag: activeFlagIds.has("low_confidence") ? "flagged" : (resolvedFlagIds.has("low_confidence") ? "resolved" : null),
    active_flag_ids: Array.from(activeFlagIds),
    resolved_flag_ids: Array.from(resolvedFlagIds),
    risk_categories: Array.from(categories),
  }

  // Last-resort fallback when category extraction is unavailable on legacy rows.
  if (vars.risk_categories.length === 0 && row.risk_category_name) {
    vars.risk_categories = [String(row.risk_category_name)]
  }

  // Last-resort fallback for old eval-item-only rows with no flag columns/JSON.
  if (!hasAnyActiveFlag && customFlags.length === 0 && row.highrisk == null && row.lowconf == null && row.askhuman == null) {
    vars.high_risk_flag = aggregateCategoryVars(evalItems, getItemStatus, evalMeta, "high_risk")
    vars.asked_human_flag = aggregateCategoryVars(evalItems, getItemStatus, evalMeta, "asked_human")
    vars.low_confidence_flag = aggregateCategoryVars(evalItems, getItemStatus, evalMeta, "low_confidence")
    if (vars.high_risk_flag === "flagged") activeFlagIds.add("high_risk")
    if (vars.low_confidence_flag === "flagged") activeFlagIds.add("low_confidence")
    if (vars.asked_human_flag === "flagged") activeFlagIds.add("asked_human")
    vars.active_flag_ids = Array.from(activeFlagIds)
    vars.escalation = activeFlagIds.size > 0 ? "true" : "false"
  }

  if (row.extractions && typeof row.extractions === "object") vars.extractions = row.extractions

  return vars
}

export interface DashboardAgent {
  id: number
  title: string | null
}

export function useSupabaseDashboardData(teamId?: string) {
  const [contacts, setContacts] = useState<RawContact[]>([])
  const [messages, setMessages] = useState<RawMessage[]>([])
  const [agents, setAgents] = useState<DashboardAgent[]>([])
  const [formFieldMeta, setFormFieldMeta] = useState<Record<string, DashboardFormFieldMeta>>({})
  const [formRecordUpdatedAt, setFormRecordUpdatedAt] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [loadingProgress, setLoadingProgress] = useState("")
  // True when the message dataset hit MAX_MESSAGE_ROWS and older messages in the
  // selected range were not loaded (so stats cover only the most recent N).
  const [truncated, setTruncated] = useState(false)

  const fetchData = useCallback(async (
    dateRange: DateRange,
    options?: { contactSourceFilter?: DashboardContactSourceFilter }
  ) => {
    const contactSourceFilter = options?.contactSourceFilter ?? "real_only"
    const contactTypes: string[] =
      contactSourceFilter === "all"
        ? ["user", "synthetic"]
        : contactSourceFilter === "synthetic_only"
          ? ["synthetic"]
          : ["user"]

    if (!teamId) {
      setError("No team selected")
      return
    }

    setLoading(true)
    setError("")
    setTruncated(false)
    setLoadingProgress("Loading messages from Supabase...")

    // Use exact timestamps; presets already bake in start/endOfDay, so re-snapping would lose picker time precision.
    const fromIso = dateRange.from ? dateRange.from.toISOString() : null
    const toIso = dateRange.to ? dateRange.to.toISOString() : null

    try {
      // Fetch messages in the date range first, then derive which contacts to load.
      // This ensures we find contacts who interacted in the period regardless of
      // when they were originally created.
      // Returns the rows plus whether we stopped early because the cap was hit
      // (i.e. more rows exist in the range than we loaded).
      // PostgREST caps each response at max_rows (1000), so page SIZE can't grow.
      // But the page REQUESTS are independent — only the stop condition is serial.
      // So we fetch a window of pages concurrently, then decide whether to fetch
      // the next window. This trades one-at-a-time round trips for ~PAGE_CONCURRENCY
      // in flight at once, while keeping memory bounded by MAX_MESSAGE_ROWS.
      const fetchPage = async (offset: number) => {
        let q = supabase
          .from("messages")
          .select("*")
          .eq("team", teamId)
          // created_at can tie across rows, so id breaks the tie and keeps
          // paging stable.
          .order("created_at", { ascending: false })
          .order("id", { ascending: false })
          .range(offset, offset + MESSAGE_PAGE_SIZE - 1)

        if (fromIso) q = q.gte("created_at", fromIso)
        if (toIso) q = q.lte("created_at", toIso)

        const { data, error: err } = await q
        if (err) throw err
        return data || []
      }

      const fetchMessageRows = async (): Promise<{ rows: any[]; truncated: boolean }> => {
        const out: any[] = []
        let truncated = false

        for (let base = 0; base < MAX_MESSAGE_ROWS; base += MESSAGE_PAGE_SIZE * PAGE_CONCURRENCY) {
          // Build this window of page offsets, clamped to the cap.
          const offsets: number[] = []
          for (let i = 0; i < PAGE_CONCURRENCY; i++) {
            const offset = base + i * MESSAGE_PAGE_SIZE
            if (offset >= MAX_MESSAGE_ROWS) break
            offsets.push(offset)
          }

          const batches = await Promise.all(offsets.map(fetchPage))

          // Append in offset order so the overall result stays sorted.
          for (const rows of batches) out.push(...rows)
          setLoadingProgress(`Loading messages… ${out.length.toLocaleString()}`)

          // Only the last page of the window proves we hit the end.
          const lastPage = batches[batches.length - 1]
          if (!lastPage || lastPage.length < MESSAGE_PAGE_SIZE) break
          // Every page in this window was full and the next window would exceed the
          // cap → more rows exist that we won't load.
          if (base + MESSAGE_PAGE_SIZE * PAGE_CONCURRENCY >= MAX_MESSAGE_ROWS) truncated = true
        }
        return { rows: out, truncated }
      }

      // Fetch contacts by explicit ID list so we capture contacts created before the
      // date range who still had activity within it.
      // Batch size of 100: each UUID is ~36 chars so 100 IDs ≈ 3.6KB in the URL,
      // well under the ~8KB PostgREST/Cloudflare limit.
      const fetchContactRowsByIds = async (ids: string[], respectTypeFilter = true): Promise<any[]> => {
        if (ids.length === 0) return []
        const BATCH = 100

        // Split the IDs into 100-ID chunks, then run those chunk-queries with
        // bounded concurrency (same cap as message pages) instead of one at a
        // time. Bounded so a contact-heavy range doesn't fire hundreds of
        // simultaneous requests.
        const chunks: string[][] = []
        for (let i = 0; i < ids.length; i += BATCH) chunks.push(ids.slice(i, i + BATCH))

        const out: any[] = []
        for (let w = 0; w < chunks.length; w += PAGE_CONCURRENCY) {
          const window = chunks.slice(w, w + PAGE_CONCURRENCY)
          const results = await Promise.all(
            window.map(async (batchIds) => {
              let q = supabase
                .from("contacts")
                .select("*")
                .eq("team", teamId)
                .in("id", batchIds)
              // Contacts that only matter because they hold form data bypass the
              // real/synthetic type filter — form & eval data captured during
              // simulation lives on synthetic contacts and should still analyze.
              if (respectTypeFilter) q = q.in("type", contactTypes)
              const { data, error: err } = await q
              if (err) throw err
              return data || []
            })
          )
          for (const rows of results) out.push(...rows)
          setLoadingProgress(`Loading contacts… ${out.length.toLocaleString()}`)
        }
        return out
      }

      const { data: evalRows, error: evalErr } = await supabase
        .from("eval_items")
        .select("id, name, highrisk, lowconf, askhuman")
      if (evalErr) throw evalErr
      const evalMeta: EvalItemMetaMap = {}
      for (const r of evalRows || []) {
        evalMeta[r.id as number] = {
          name: r.name ?? "",
          highrisk: r.highrisk,
          lowconf: r.lowconf,
          askhuman: r.askhuman,
        }
      }

      const { data: agentRows, error: agentErr } = await supabase
        .from("agents")
        .select("id, title")
        .eq("team_id", teamId)
        // Exclude deactivated (archived) agents; null status counts as active.
        .or("status.is.null,status.neq.archived")
      if (agentErr) throw agentErr
      setAgents((agentRows || []).map((r: any) => ({ id: r.id as number, title: r.title ?? null })))

      const { rows: messageRows, truncated: messagesTruncated } = await fetchMessageRows()
      setTruncated(messagesTruncated)

      // Derive the set of contact IDs that had activity in the date range.
      const activeContactIds = [...new Set(
        messageRows.map((r: any) => r.contact).filter(Boolean) as string[]
      )]

      // Form fields surface as dashboard variables namespaced "form.<field>".
      // Load the whole team's form records so captured forms show even when the
      // contact who filled them had no message in the selected date range.
      const { valuesByContact: formValuesByContact, contactIds: formContactIds, formIds: referencedFormIds, updatedAt: nextFormRecordUpdatedAt } =
        await fetchTeamFormValues(teamId, activeContactIds)
      setFormRecordUpdatedAt(nextFormRecordUpdatedAt)

      // Contacts active in range respect the real/synthetic source filter.
      // Any contact that holds form data is loaded regardless of type (even if
      // also active), so form & eval data captured during simulation — which
      // lives on synthetic contacts — still shows in the Variables tab.
      const [activeContactRows, formContactRows] = await Promise.all([
        fetchContactRowsByIds(activeContactIds, true),
        fetchContactRowsByIds([...formContactIds], false),
      ])
      const contactRowById = new Map<string, any>()
      for (const r of [...activeContactRows, ...formContactRows]) contactRowById.set(r.id, r)
      const contactRows = Array.from(contactRowById.values())

      // Field titles + option labels. Loaded by team AND by the exact form ids the
      // records reference, so definitions resolve even when a form's team is null.
      const nextFormFieldMeta = await fetchFormFieldMeta(teamId, Array.from(referencedFormIds))
      setFormFieldMeta(nextFormFieldMeta)

      // Any loaded form value whose field has no matching definition is an orphan
      // (e.g. from a deleted/test form); flag it in the console for diagnosis.
      const knownFormKeys = new Set(Object.keys(nextFormFieldMeta))
      const orphanKeys = new Set<string>()
      for (const flat of formValuesByContact.values()) {
        for (const k of Object.keys(flat)) if (!knownFormKeys.has(k)) orphanKeys.add(k)
      }
      if (orphanKeys.size > 0) {
        console.warn(
          `[dashboard] ${orphanKeys.size} form value key(s) have no matching field in any team form ` +
          `(likely a deleted or test form): ${Array.from(orphanKeys).join(", ")}`
        )
      }

      const nextContacts: RawContact[] = (contactRows || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        phone_number: row.id,
        severity: row.severity ?? null,
        time_created: toUnix(row.created_at),
        last_message_time: toUnix(row.lasteval || row.created_at),
        conversation_status: row.no_reply_needed ? "no_reply_needed" : "active",
        vars: { ...parseContactVars(row), ...(formValuesByContact.get(row.id) || {}) },
        _projectId: "supabase",
        _projectName: "Supabase",
        // Carried through even for contacts pulled in only for their form data
        // (which bypasses the type filter) — see the RawContact.type doc comment.
        type: row.type ?? null,
      }))

      const includedContactIds = new Set((contactRows || []).map((row: any) => row.id))

      const nextMessages: RawMessage[] = (messageRows || [])
        .map((row: any) => {
          const direction: RawMessage["direction"] =
            row.role === "assistant" || row.role === "human" ? "outgoing" : "incoming"
          return {
            id: row.id,
            contact_id: row.contact,
            agent_id: row.agent ?? null,
            direction,
            role: row.role ?? null,
            sender: row.sender ?? null,
            content: row.message || "",
            time_created: toUnix(row.created_at),
            channel: row.channel ?? null,
            integration: (row.integration && typeof row.integration === "object") ? row.integration : null,
            vars: mapMessageFlags(row, evalMeta),
            _projectId: "supabase",
            _projectName: "Supabase",
          }
        })
        .filter((m) => !!m.contact_id && includedContactIds.has(m.contact_id))

      // Keep latest-message metadata available on contacts.
      const byContact = new Map<string, RawMessage[]>()
      nextMessages.forEach((m) => {
        if (!byContact.has(m.contact_id)) byContact.set(m.contact_id, [])
        byContact.get(m.contact_id)!.push(m)
      })

      nextContacts.forEach((c) => {
        const msgList = byContact.get(c.id) || []
        c.message_count = msgList.length
        c.incoming_message_count = msgList.filter((m) => m.direction === "incoming").length
        c.outgoing_message_count = msgList.filter((m) => m.direction === "outgoing").length
        c.last_message_time = msgList.length > 0 ? Math.max(...msgList.map((m) => m.time_created)) : c.last_message_time
      })

      setContacts(nextContacts)
      setMessages(nextMessages)
      setLoadingProgress(`Loaded ${nextContacts.length.toLocaleString()} contacts and ${nextMessages.length.toLocaleString()} messages`)
    } catch (err) {
      console.error("[useSupabaseDashboardData] failed:", err)
      // Supabase errors are plain objects with a `message` field, not Error instances.
      const raw = err instanceof Error
        ? err.message
        : (err as any)?.message || (err as any)?.error_description || JSON.stringify(err)
      const base = (raw || "Failed to load dashboard data").trim()
      const isGateway = /502|bad gateway|503|504|incomplete|fetch failed/i.test(base)
      setError(isGateway ? `${base} Try a shorter date range if this persists.` : base)
    } finally {
      setLoading(false)
      setTimeout(() => setLoadingProgress(""), 3000)
    }
  }, [teamId])

  const clearData = useCallback(() => {
    setContacts([])
    setMessages([])
    setAgents([])
    setFormFieldMeta({})
    setFormRecordUpdatedAt({})
    setError("")
    setTruncated(false)
    setLoadingProgress("")
  }, [])

  return {
    contacts,
    messages,
    agents,
    /** Field titles + option labels for form.* variables, keyed by "form.<field>". */
    formFieldMeta,
    /** Last update time of each form record, keyed by "<contact>:<form>". */
    formRecordUpdatedAt,
    loading,
    error,
    loadingProgress,
    projectLoadProgress: {},
    /** Message dataset was capped — stats cover only the most recent maxMessageRows. */
    truncated,
    /** The message-row ceiling that triggers truncation. */
    maxMessageRows: MAX_MESSAGE_ROWS,
    fetchData,
    clearData,
    setError,
  }
}

